import IconAdd from "components/EvtKit/icons/IconAdd"
import Input from "components/EvtKit/input/Input"
import BodyText from "components/Typography/BodyText"
import ActionsButtons from "pages/products/components/ActionButtons"
import { DataTypesOptions } from "constants/integrations"
import { memo, useCallback, useState } from "react"
import styled from "styled-components"
import clsx from "clsx"
import Heading from "components/Typography/Heading"
import SelectV2, { Option } from "components/EvtKit/select/SelectV2"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
`
const ContainerAdd = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`
const InputsContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 12px;
  align-items: center;
  align-content: center;
  margin-top: 24px;
`

const FileInput = styled.div`
  width: 100%;
  display: flex;
  justify-content: start;
  padding-left: 4px;
`
const IntegrationsContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`

const AddButton = styled.button`
  cursor: pointer;
  &.disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

const IntegrationsList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  padding-bottom: 8px;
  margin-top: 8px;

  &::-webkit-scrollbar {
    width: 4px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: white;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.palette.gray_light};
    border-radius: 10px;
    cursor: pointer;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${(props) => props.theme.palette.gray_medium};
  }
`

const SelectContainer = styled.div`
  width: 100%;
`

interface Props {
  integrationList: App.Integrations[]
  setIntegrationList: React.Dispatch<React.SetStateAction<App.Integrations[]>>
  disabled?: boolean
  modalType?: string
}
const IntegrationForm = (props: Props) => {
  const { integrationList, setIntegrationList, disabled = false, modalType } = props
  const [editKey, setEditKey] = useState<string | null>(null)
  const [isAddingNew, setIsAddingNew] = useState(false)

  const onIntegrationListChange = useCallback(
    (newFederationFields: App.Integrations[]) => {
      setIntegrationList(newFederationFields)
    },
    [setIntegrationList]
  )

  const handleInputChange = useCallback(
    (index: number, name: string, value: string) => {
      setIntegrationList((prevIntegrationList) => {
        const newIntegrationList = [...prevIntegrationList]

        newIntegrationList[index][name] = value
        onIntegrationListChange(newIntegrationList)

        return newIntegrationList
      })
    },
    [setIntegrationList, onIntegrationListChange]
  )

  const handleClick = useCallback(() => {
    setIsAddingNew(true)
    setIntegrationList((prevIntegrationList) => [
      ...prevIntegrationList,
      { key: "", label: "", type: "", externalEndpointUrlForFileManagement: "" }
    ])
  }, [setIntegrationList])

  const handleSelectChange = useCallback(
    (index: number) => (option?: Option | null) => {
      const optionValue = option?.value

      if (optionValue) {
        setIntegrationList((prevIntegrationList) => {
          const newIntegrationList = [...prevIntegrationList]

          newIntegrationList[index].type = optionValue

          return newIntegrationList
        })
      }
    },
    [setIntegrationList]
  )

  return (
    <>
      <Container>
        <Heading variant="h6">Integrações</Heading>
        <AddButton
          type="button"
          onClick={handleClick}
          disabled={disabled}
          className={clsx({
            disabled: disabled
          })}
        >
          <ContainerAdd>
            <IconAdd />
            <BodyText size="small" weight="regular" color="primary-dark">
              Adicionar
            </BodyText>
          </ContainerAdd>
        </AddButton>
      </Container>
      <IntegrationsList>
        {integrationList.map((integration, index) => {
          const showFileInput = integration.type === "file"
          const isEditing = editKey === integration.key
          const isUpdateMode = modalType === "update"

          const isFieldDisabled = (field: "label" | "key" | "externalEndpointUrlForFileManagement" | "select") => {
            return (
              props.disabled ||
              (isUpdateMode && isEditing && (field === "key" || field === "select")) ||
              (isUpdateMode && !isEditing && (index < integrationList.length - 1 || !isAddingNew)) ||
              (modalType === "create" && !isAddingNew)
            )
          }

          return (
            <IntegrationsContainer key={index}>
              <InputsContainer>
                <SelectContainer>
                  <SelectV2
                    id={`integrationType-${index}`}
                    options={DataTypesOptions}
                    value={integration.type}
                    onChange={handleSelectChange(index)}
                    label="Selecione o tipo da integração *"
                    disabled={isFieldDisabled("select")}
                    className="fullWidth"
                  />
                </SelectContainer>
                <Input
                  type="text"
                  name="label"
                  className="fullWidth search-input"
                  value={integration.label}
                  onChange={(e) => handleInputChange(index, e.target.name, e.target.value)}
                  label="Label *"
                  required
                  placeholder="Digite"
                  shrink
                  disabled={isFieldDisabled("label")}
                />
                <Input
                  type="text"
                  name="key"
                  className="fullWidth search-input"
                  value={integration.key}
                  onChange={(e) => handleInputChange(index, e.target.name, e.target.value)}
                  label="Key *"
                  required
                  placeholder="Digite"
                  shrink
                  disabled={isFieldDisabled("key")}
                />
                {showFileInput && (
                  <FileInput>
                    <Input
                      type="text"
                      id="externalEndpointUrlForFileManagement"
                      name="externalEndpointUrlForFileManagement"
                      className="fullWidth search-input"
                      label="Url para envio de arquivos *"
                      value={integration.externalEndpointUrlForFileManagement}
                      required={false}
                      placeholder="Digite"
                      shrink
                      disabled={isFieldDisabled("externalEndpointUrlForFileManagement")}
                      onChange={(e) => handleInputChange(index, e.target.name, e.target.value)}
                    />
                  </FileInput>
                )}
              </InputsContainer>
              <ActionsButtons
                isEditing={isEditing}
                setEditKey={setEditKey}
                modalType={modalType}
                index={index}
                integration={integration}
                integrationList={integrationList}
                setIntegrationList={setIntegrationList}
                onIntegrationListChange={onIntegrationListChange}
                isAddingNew={isAddingNew && index === integrationList.length - 1}
              />
            </IntegrationsContainer>
          )
        })}
      </IntegrationsList>
    </>
  )
}

export default memo(IntegrationForm)
